import DeferRenderUntilIntersecting from '@/components/DeferRenderUntilIntersecting';
import Tooltip from '@/components/Tooltip';
import useGlossaryTerms from '@/hooks/useGlossaryTerms';
import { useValuationData } from '@/pages/CompanyReport/components/Valuation';
import { shouldSkipDefer } from '@/redux/ui/selectors/shouldSkipDefer';
import loadable from '@loadable/component';
import { Grid, useTheme } from '@simplywallst/ui-core';
import { shallowEqual, useSelector } from 'react-redux';
import { useBaseMetricLabel, useMetricRatioLabel } from '../../hooks';
import { ChartContainer, Growth, Term, TipContainer, Value } from './styled';
import { useSecondMetricLabel } from '../../hooks/useLabel';

const DonutChart = loadable(() => import('@/components/DonutChart'), {
  resolveComponent: (component) => component.DonutChart,
});

const isMetric = (
  metric: string
): metric is 'PB' | 'PS' | 'Forward PS' | 'PE' | 'Forward PE' => {
  return ['PB', 'PS', 'Forward PS', 'PE', 'Forward PE'].includes(metric);
};

interface Props {
  metric: string;
  value: string;
}

export const KeyMetricsChart = ({
  metric: metricProp,
  value: value,
}: Props) => {
  const metric = isMetric(metricProp) ? metricProp : 'n/a';

  const getTerm = useGlossaryTerms();

  const selected = useValuationData();

  const skipDefer = useSelector<
    Parameters<typeof shouldSkipDefer>[0],
    ReturnType<typeof shouldSkipDefer>
  >(shouldSkipDefer, shallowEqual);

  const theme = useTheme();

  const getBaseMetricLabel = useBaseMetricLabel();
  const getMetricRatioLabel = useMetricRatioLabel();
  const getSecondMetricLabel = useSecondMetricLabel();

  const baseArc = {
    label: getSecondMetricLabel(metric) || 'n/a',
    formattedValue: selected.metricsData[metric]?.formattedSecondaryValue,
    rawValue: selected.metricsData[metric]?.secondaryValue,
    color: theme.color.chart01,
  };

  const innerArc = {
    label: getBaseMetricLabel(selected.metricsData[metric]?.label) || 'n/a',
    formattedValue: selected.metricsData[metric]?.formattedValue || 'n/a',
    rawValue: selected.metricsData[metric]?.value || 0,
    color: theme.color.chart01,
  };

  return (
    <Grid
      templateRows="auto"
      templateColumns="auto 80px"
      templateColumnsSm="auto 280px"
      templateColumnsMd="auto 80px"
      templateColumnsLg="auto 200px"
    >
      <Grid alignContent="start" justifyContent="center">
        <DeferRenderUntilIntersecting skipDefer={skipDefer}>
          {() => (
            <ChartContainer data-cy-id="donut-chart-container">
              <DonutChart
                baseArc={metric === 'Forward PE' ? innerArc : baseArc}
                innerArc={metric === 'Forward PE' ? baseArc : innerArc}
              />
            </ChartContainer>
          )}
        </DeferRenderUntilIntersecting>
      </Grid>
      <Grid alignContent="center" justifyContent="start">
        <Value data-cy-id="key-metric-value">{value}</Value>

        <Tooltip
          openDelay={300}
          light
          title={
            <TipContainer>
              {getTerm(
                `${
                  metric.toLowerCase() as Lowercase<typeof metric>
                } ratio` as const
              )}
            </TipContainer>
          }
        >
          <Term data-cy-id="key-metric-term">
            {getMetricRatioLabel(metric)}
          </Term>
        </Tooltip>
        <Growth data-cy-id="key-metric-growth">
          {metric === 'Forward PE' &&
            selected.metricsData[metric].formattedGrowth !== 'n/a' &&
            `${selected.metricsData[metric].formattedGrowth} earnings`}
          {metric === 'Forward PS' &&
            selected.metricsData[metric].formattedGrowth !== 'n/a' &&
            `${selected.metricsData[metric].formattedGrowth} sales`}
        </Growth>
      </Grid>
    </Grid>
  );
};
